/**
 * SplashScreen Component
 *
 * Branded loading screen displayed during app initialization.
 * Features SOKA logo with dark blur aesthetic, rotating sync icon,
 * and animated loading dots matching the in-game sync overlay style.
 */

import { motion } from 'framer-motion';
import { sokaLogo } from '@/assets';

interface SplashScreenProps {
  progress?: number; // 0-100, optional progress indicator
}

export function SplashScreen({ progress }: SplashScreenProps) {
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-b from-[#0A2E1B] to-[#145C32] overflow-hidden">
      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center justify-center px-4">
        {/* SOKA Logo with glow */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="relative mb-8"
        >
          {/* Glow effect behind logo */}
          <motion.div
            className="absolute inset-0 blur-3xl opacity-30"
            animate={{
              scale: [1, 1.15, 1],
              opacity: [0.2, 0.35, 0.2]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
          >
            <div className="w-full h-full bg-yellow-400 rounded-full" />
          </motion.div>

          {/* Logo */}
          <motion.img
            src={sokaLogo}
            alt="SOKA"
            className="w-44 h-auto object-contain relative z-10"
            animate={{ y: [0, -6, 0] }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
          />
        </motion.div>

        {/* LUDOKA Title */}
        <motion.h1
          className="font-mind-boggle text-4xl text-white text-center mb-6"
          style={{
            textShadow:
              '3px 3px 0px #000, -1px -1px 0px #000, 1px -1px 0px #000, -1px 1px 0px #000, 2px 2px 6px rgba(0,0,0,0.5)'
          }}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          LUDOKA
        </motion.h1>

        {/* Rotating sync icon */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="mb-5"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          >
            <svg
              width="36"
              height="36"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-yellow-400"
            >
              <path
                d="M21 2V8H15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3 12C3 7.02944 7.02944 3 12 3C15.3652 3 18.2834 4.94021 19.7082 7.76001L21 8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3 22V16H9"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M21 12C21 16.9706 16.9706 21 12 21C8.63476 21 5.71659 19.0598 4.29181 16.24L3 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.div>
        </motion.div>

        {/* Progress Bar - Only show if progress is provided */}
        {progress !== undefined && (
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: '240px' }}
            transition={{ delay: 0.4, duration: 0.4 }}
            className="relative h-1.5 bg-white/15 rounded-full overflow-hidden mb-5"
          >
            {/* Progress fill */}
            <motion.div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-yellow-400 to-yellow-300 rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: `${progress.toString()}%` }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            />

            {/* Shimmer effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent"
              animate={{ x: ['-100%', '200%'] }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: 'linear'
              }}
            />
          </motion.div>
        )}

        {/* Animated loading dots */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          className="flex gap-2"
        >
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-2.5 h-2.5 rounded-full bg-yellow-400"
              animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
              transition={{
                duration: 1,
                repeat: Infinity,
                delay: i * 0.2,
                ease: 'easeInOut'
              }}
            />
          ))}
        </motion.div>
      </div>
    </div>
  );
}
